/**
 * Judgment Analytics — read model
 * ===============================
 *
 * Turns raw Decision Intelligence rows (signals + outcomes + attributions) into
 * the leader-facing analytics: a judgment profile per person × type × domain
 * cell, and a trust map that answers "whose judgment should I seek for *this*
 * kind of call?".
 *
 * Computed on read from the tested model layer (`judgmentScoring`,
 * `judgmentTaxonomy`, `judgmentLens`) — the scoring math is never reimplemented
 * in SQL, so there is a single source of truth. The functions here are pure over
 * plain row shapes; the Supabase loader that feeds them lives in the service.
 *
 * Design spec: docs/9-exploration/judgment-analytics/DESIGN.md
 */

import type { JudgmentDomain, JudgmentTier } from '../../types/decisionIntelligence';
import type { JudgmentType, JudgmentLens } from './judgmentTaxonomy';
import { judgmentCellKey, leadDimensionForType, isJudgmentType } from './judgmentTaxonomy';
import {
  beliefProbability,
  calibrationScore,
  brierDecomposition,
  meanConsensusEdge,
  insightProfile,
  insightEmployability,
  classifyTier,
  overconfidence,
  confidenceWeight,
  type ForecastVsCrowd,
  type ResolvedForecast,
  type InsightArchetype,
} from './judgmentScoring';
import { inferLensScores, type LensFeatures } from './judgmentLens';

type Recommendation = 'proceed' | 'pause' | 'stop' | 'modify';

/** The minimal signal shape the analytics needs (a subset of DecisionSignal + attribution). */
export interface AnalyticsSignal {
  actorId: string;
  actorEmail?: string;
  judgmentType: JudgmentType;
  domain: JudgmentDomain;
  recommendation: Recommendation;
  /** Stated confidence, normalised to [0,1]. */
  confidence: number;
  /** Stakes, normalised to [0,1], if captured. */
  stakes?: number | null;
  riskFlagCount: number;
  /** Present only once the call has resolved. */
  resolved?: {
    /** Realized binary outcome (1 = positive). */
    outcome: 0 | 1;
    /** Persisted consensus reference (leave-one-out peer mean belief), [0,1]. */
    consensusProbability: number;
    /** Persisted number of peers on the same call. */
    peerCount: number;
  } | null;
}

export interface JudgmentCellProfile {
  cellKey: string;
  type: JudgmentType;
  domain: JudgmentDomain;
  actorId: string;
  actorEmail?: string;
  signalCount: number;
  resolvedCount: number;
  calibration: number | null;
  resolution: number | null;
  meanConsensusEdge: number | null;
  insightEdge: number | null;
  insightArchetype: InsightArchetype;
  baselineAlignment: number;
  divergentAccuracy: number | null;
  overconfidence: number | null;
  tier: JudgmentTier;
  dominantLens: JudgmentLens | null;
  /** Ranking scalar for "whose insight to employ" on this cell. */
  employability: number;
}

/** Direction-aware belief probability for one signal. */
function signalBelief(s: AnalyticsSignal): number {
  return beliefProbability(s.recommendation, s.confidence);
}

/**
 * Build one cell profile from a person's signals within a single
 * `type × domain` cell. Pure; callers group signals into cells first.
 */
export function computeCellProfile(
  actorId: string,
  type: JudgmentType,
  domain: JudgmentDomain,
  signals: AnalyticsSignal[],
  actorEmail?: string,
): JudgmentCellProfile {
  const resolvedSignals = signals.filter((s) => s.resolved);

  const forecasts: ResolvedForecast[] = resolvedSignals.map((s) => ({
    p: signalBelief(s),
    outcome: s.resolved!.outcome,
  }));
  const vsCrowd: ForecastVsCrowd[] = resolvedSignals.map((s) => ({
    individual: signalBelief(s),
    crowd: s.resolved!.consensusProbability,
    outcome: s.resolved!.outcome,
  }));

  const calibration = calibrationScore(forecasts);
  const decomp = brierDecomposition(forecasts);
  const insight = insightProfile(vsCrowd);
  const accuracy =
    forecasts.length === 0
      ? 0
      : forecasts.filter((f) => (f.p >= 0.5 ? f.outcome === 1 : f.outcome === 0)).length /
        forecasts.length;

  const meanConf =
    resolvedSignals.length === 0
      ? 0
      : resolvedSignals.reduce((a, s) => a + s.confidence, 0) / resolvedSignals.length;

  const tier = classifyTier({
    calibration,
    resolution: decomp?.resolution ?? null,
    resolvedCount: forecasts.length,
  });

  // Lens from the full signal set (resolved or not — stance shows in behaviour).
  const proceed = signals.filter((s) => s.recommendation === 'proceed').length;
  const caution = signals.filter(
    (s) => s.recommendation === 'pause' || s.recommendation === 'stop',
  ).length;
  const meanRiskFlags =
    signals.length === 0 ? 0 : signals.reduce((a, s) => a + s.riskFlagCount, 0) / signals.length;
  const features: LensFeatures = {
    proceedRate: signals.length ? proceed / signals.length : 0,
    cautionRate: signals.length ? caution / signals.length : 0,
    riskFlagDensity: Math.min(1, meanRiskFlags / 5),
    meanConfidence:
      signals.length === 0 ? 0 : signals.reduce((a, s) => a + s.confidence, 0) / signals.length,
    consensusCorrelation: 1 - insight.divergentShare * 2, // proxy: less divergence → more aligned
    contrarianRate: insight.divergentShare,
    calibration: calibration ?? undefined,
  };
  const dominantLens =
    signals.length === 0 ? null : inferLensScores(features)[0]?.lens ?? null;

  return {
    cellKey: judgmentCellKey({ type, domain }),
    type,
    domain,
    actorId,
    actorEmail,
    signalCount: signals.length,
    resolvedCount: forecasts.length,
    calibration,
    resolution: decomp?.resolution ?? null,
    meanConsensusEdge: meanConsensusEdge(vsCrowd),
    insightEdge: insight.insightEdge,
    insightArchetype: insight.archetype,
    baselineAlignment: insight.baselineAlignment,
    divergentAccuracy: insight.divergentAccuracy,
    overconfidence: forecasts.length === 0 ? null : overconfidence(meanConf, accuracy),
    tier,
    dominantLens,
    employability: insightEmployability(insight),
  };
}

/** Group signals into `actor × type × domain` cells and compute every profile. */
export function buildCellProfiles(signals: AnalyticsSignal[]): JudgmentCellProfile[] {
  const groups = new Map<string, AnalyticsSignal[]>();
  const emails = new Map<string, string | undefined>();
  for (const s of signals) {
    const type = isJudgmentType(s.judgmentType) ? s.judgmentType : 'outcome_prediction';
    const key = `${s.actorId}::${type}::${s.domain}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push({ ...s, judgmentType: type });
    if (s.actorEmail) emails.set(s.actorId, s.actorEmail);
  }

  const profiles: JudgmentCellProfile[] = [];
  for (const [key, cellSignals] of groups) {
    const [actorId, type, domain] = key.split('::');
    profiles.push(
      computeCellProfile(
        actorId,
        type as JudgmentType,
        domain as JudgmentDomain,
        cellSignals,
        emails.get(actorId),
      ),
    );
  }
  return profiles;
}

export interface TrustMapEntry {
  cellKey: string;
  type: JudgmentType;
  domain: JudgmentDomain;
  leadDimension: string;
  /** Profiles ranked best-first for this cell by employability, then the lead dimension. */
  ranked: JudgmentCellProfile[];
}

/**
 * The leader trust map: for each `type × domain` cell, the people ranked by
 * whose judgment to weight. Ranks by insight employability first (the
 * aligned-teammate-with-a-proven-edge sweet spot, which scores reflexive
 * contrarians at 0), then by the cell's lead scoring dimension, then by how much
 * resolved evidence backs the profile. Cells with no resolved evidence are
 * omitted.
 */
export function buildTrustMap(profiles: JudgmentCellProfile[]): TrustMapEntry[] {
  const byCell = new Map<string, JudgmentCellProfile[]>();
  for (const p of profiles) {
    if (p.resolvedCount === 0) continue;
    if (!byCell.has(p.cellKey)) byCell.set(p.cellKey, []);
    byCell.get(p.cellKey)!.push(p);
  }

  const entries: TrustMapEntry[] = [];
  for (const [cellKey, cellProfiles] of byCell) {
    const { type, domain } = cellProfiles[0];
    const leadDimension = leadDimensionForType(type);
    const ranked = [...cellProfiles].sort((a, b) => {
      if (b.employability !== a.employability) return b.employability - a.employability;
      const av = leadValue(a, leadDimension);
      const bv = leadValue(b, leadDimension);
      if (bv !== av) return bv - av;
      return b.resolvedCount - a.resolvedCount;
    });
    entries.push({ cellKey, type, domain, leadDimension, ranked });
  }
  return entries.sort((a, b) => a.cellKey.localeCompare(b.cellKey));
}

function leadValue(p: JudgmentCellProfile, dimension: string): number {
  switch (dimension) {
    case 'insight':
      return p.employability;
    case 'consensus_edge':
      return (p.meanConsensusEdge ?? 0) * confidenceWeight(p.resolvedCount);
    case 'calibration':
      return p.calibration ?? 0;
    case 'resolution':
      return p.resolution ?? 0;
    default:
      return p.calibration ?? 0;
  }
}
